/**
 * TerraMatrix Universal Data Platform - TemplatesPanel Component
 *
 * Panel component for the Templates tab showing saved query templates.
 * Allows browsing, executing, and deleting templates.
 *
 * Phase 5: Data Page UI Integration
 */

import React, { useState } from 'react';
import { useQueryTemplateList, useDeleteQueryTemplate } from '@/hooks/data/useQueryTemplate';
import { TemplateExecutionModal } from './TemplateExecutionModal';
import type { QueryTemplate, TemplateExecutionResult } from '@/services/api/queryTemplateApi';

interface TemplatesPanelProps {
  /** Optional class name */
  className?: string;
  /** Callback when a template is executed successfully */
  onTemplateExecuted?: (result: TemplateExecutionResult, template: QueryTemplate) => void;
}

export const TemplatesPanel: React.FC<TemplatesPanelProps> = ({ className = '', onTemplateExecuted }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<QueryTemplate | null>(null);
  const [showExecuteModal, setShowExecuteModal] = useState(false);

  // Fetch templates
  const {
    data: templatesData,
    isLoading,
    error,
    refetch,
  } = useQueryTemplateList({
    search: searchQuery || undefined,
    sortBy: 'usageCount',
    sortOrder: 'desc',
  });

  const deleteTemplateMutation = useDeleteQueryTemplate();

  const templates = templatesData?.templates || [];

  const handleExecute = (template: QueryTemplate) => {
    setSelectedTemplate(template);
    setShowExecuteModal(true);
  };

  const handleDelete = async (template: QueryTemplate) => {
    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      await deleteTemplateMutation.mutateAsync(template.id);
      refetch();
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  const formatTimeAgo = (dateString?: string) => {
    if (!dateString) return 'Never used';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const getVariableSummary = (template: QueryTemplate) => {
    const vars = Object.entries(template.variables || {})
      .filter(([, def]) => def.type !== 'system')
      .map(([key, def]) => `${key} (${def.default || 'required'})`);

    if (vars.length === 0) return 'No variables';
    if (vars.length <= 2) return vars.join(', ');
    return `${vars.slice(0, 2).join(', ')} +${vars.length - 2} more`;
  };

  return (
    <div className={`flex flex-col min-h-0 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">Query Templates</h2>
            <p className="text-sm text-muted-foreground">
              Saved queries with configurable variables
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Refresh templates"
          >
            <svg
              className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-border bg-background
                       focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
      </div>

      {/* Templates List */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <svg className="w-8 h-8 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-destructive mb-2">Failed to load templates</div>
            <button
              onClick={() => refetch()}
              className="text-sm text-primary hover:underline"
            >
              Try again
            </button>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-muted-foreground">No templates found</p>
            <p className="text-sm text-muted-foreground mt-1">
              {searchQuery
                ? 'Try adjusting your search'
                : 'Run a query and save it as a template'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {templates.map((template) => (
              <div
                key={template.id}
                className="group p-4 rounded-lg border border-border bg-card hover:bg-muted/30
                           transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{template.name}</h3>
                    {template.description && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {template.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                          />
                        </svg>
                        {getVariableSummary(template)}
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {formatTimeAgo(template.lastUsedAt)}
                      </span>
                      {template.usageCount > 0 && (
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 10V3L4 14h7v7l9-11h-7z"
                            />
                          </svg>
                          {template.usageCount} runs
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleExecute(template)}
                      className="p-2 rounded-lg hover:bg-primary/10 text-primary transition-colors"
                      aria-label="Execute template"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(template)}
                      disabled={deleteTemplateMutation.isPending}
                      className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground
                                 hover:text-destructive transition-colors
                                 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Delete template"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Tags */}
                {template.tags && template.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {template.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Execution Modal */}
      <TemplateExecutionModal
        open={showExecuteModal}
        onClose={() => {
          setShowExecuteModal(false);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
        onExecuted={(result) => {
          // Close the modal
          setShowExecuteModal(false);

          // Pass results up to parent (DataPage) with template info
          if (selectedTemplate && onTemplateExecuted) {
            onTemplateExecuted(result, selectedTemplate);
          }

          // Clear selected template
          setSelectedTemplate(null);

          // Refresh to update usage count
          refetch();
        }}
      />
    </div>
  );
};

export default TemplatesPanel;
